/* Storefront integration. Never submits or repeats an add-to-cart request. */
(() => {
  'use strict';
  const host = document.getElementById('redd-cart');
  if (!host || window.ReddCart || !host.attachShadow || !window.HTMLDialogElement) return;
  const cartUrl = new URL(host.dataset.cartUrl, location.origin);
  const onCartPage = location.pathname.replace(/\/$/, '') === cartUrl.pathname.replace(/\/$/, '');
  if (onCartPage && new URLSearchParams(location.search).has('redd-cart-fallback')) return;
  // Restore focus after closing the drawer, but show the ring only for keyboard use.
  document.addEventListener('pointerdown', () => document.documentElement.classList.remove('redd-keyboard-focus'), true);
  document.addEventListener('keydown', event => {
    if (!event.metaKey && !event.ctrlKey && !event.altKey) document.documentElement.classList.add('redd-keyboard-focus');
  }, true);
  let loading;
  let opening;
  let pendingAdded = false;
  let trigger;

  function resource(tag, attributes, parent = document.head) {
    return new Promise((resolve, reject) => {
      const node = document.createElement(tag);
      Object.assign(node, attributes);
      const timer = setTimeout(() => reject(new Error('Cart assets timed out')), 15000);
      node.onload = () => { clearTimeout(timer); resolve(); };
      node.onerror = () => { clearTimeout(timer); reject(new Error('Cart assets unavailable')); };
      parent.append(node);
    });
  }
  function load() {
    if (!loading) loading = (async () => {
      const surface = host.attachShadow({ mode: 'open' });
      surface.append(host.querySelector('template').content.cloneNode(true));
      await Promise.all([
        resource('link', { rel: 'stylesheet', href: host.dataset.styleSrc }, surface),
        ...(host.dataset.themeStyleSrc ? [resource('link', { rel: 'stylesheet', href: host.dataset.themeStyleSrc }, surface)] : []),
        resource('script', { src: host.dataset.coreSrc }),
      ]);
      await resource('script', { src: host.dataset.drawerSrc });
      if (!window.ReddCartDrawer) throw new Error('Cart did not initialise');
    })();
    return loading;
  }
  function fallback() {
    document.body.classList.remove('sc-is-open');
    // A separate, server-rendered form remains usable without any cart JavaScript.
    cartUrl.searchParams.set('redd-cart-fallback', '1');
    location.assign(cartUrl.href);
  }
  function open(source, added = false) {
    trigger = source || trigger || document.activeElement;
    pendingAdded ||= added;
    if (!opening) {
      opening = load().then(() => {
        const hasAdded = pendingAdded;
        pendingAdded = false;
        opening = null;
        window.dispatchEvent(new CustomEvent('redd:cart-opening'));
        window.ReddCartDrawer.open(trigger, hasAdded);
        const page = document.querySelector('[data-redd-cart-page]');
        if (page) {
          page.querySelector('[data-cart-fallback]').hidden = true;
          page.querySelector('[data-cart-launcher]').hidden = false;
        }
      }).catch(fallback);
    }
    return opening;
  }
  window.ReddCart = { open, added: source => open(source, true) };

  // Enhance REDD's native form once. A failed/uncertain write is never replayed.
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-redd-product-form]');
    if (!form || event.defaultPrevented || !window.fetch) return;
    event.preventDefault();
    if (form.dataset.submitting) return;
    const button = form.querySelector('[type="submit"]');
    const error = form.parentElement.querySelector('[data-redd-cart-error]');
    const source = event.submitter || button;
    const label = button.textContent;
    form.dataset.submitting = 'true';
    const data = new FormData(form);
    data.delete('return_to');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = host.dataset.adding;
    if (error) error.hidden = true;
    try {
      const response = await fetch(`${host.dataset.root.replace(/\/$/, '')}/cart/add.js`, {
        method: 'POST', body: data, credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Redd-Cart': 'product-form' },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : host.dataset.addError);
      await open(source, true);
    } catch (failure) {
      if (error) {
        error.textContent = failure.message || host.dataset.addError;
        error.hidden = false;
        error.scrollIntoView({block: 'center', behavior: 'smooth'});
      }
    } finally {
      delete form.dataset.submitting;
      const selected = form.querySelector('select[name="id"]')?.selectedOptions[0];
      button.disabled = selected?.dataset.available === 'false';
      button.textContent = button.disabled ? form.dataset.soldOut : label;
      button.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== cartUrl.origin || url.pathname.replace(/\/$/, '') !== cartUrl.pathname.replace(/\/$/, '') || url.search || url.hash) return;
    event.preventDefault();
    void open(link);
  });

  // Sticky buttons and page builders may use their own Ajax rather than Empire.
  // Observe successful same-origin adds, preserving their promises, payloads and errors.
  function isAdd(url, method) {
    try {
      const parsed = new URL(url, location.href);
      return String(method || 'GET').toUpperCase() === 'POST' && parsed.origin === location.origin && /\/cart\/add(?:\.js)?\/?$/.test(parsed.pathname);
    } catch (_) { return false; }
  }
  function addedResponse(data) {
    if (data && !(Number(data.status) >= 400) && (data.id || Array.isArray(data.items))) window.ReddCart.added(document.activeElement);
  }
  const originalFetch = window.fetch;
  if (originalFetch) window.fetch = function (input, options) {
    const result = originalFetch.apply(this, arguments);
    try {
      const headers = new Headers(options?.headers || input?.headers);
      if (!headers.has('X-Redd-Cart') && isAdd(input?.url || input, options?.method || input?.method)) {
        result.then(response => {
          if (response.ok) return response.clone().json().then(addedResponse);
        }).catch(() => {});
      }
    } catch (_) { /* Observation must never change the caller's fetch behaviour. */ }
    return result;
  };
  if (window.XMLHttpRequest) {
    const prototype = window.XMLHttpRequest.prototype;
    const originalOpen = prototype.open;
    const originalHeader = prototype.setRequestHeader;
    const originalSend = prototype.send;
    const requests = new WeakMap();
    prototype.open = function (method, url) {
      requests.set(this, { add: isAdd(url, method), owned: false });
      return originalOpen.apply(this, arguments);
    };
    prototype.setRequestHeader = function (name) {
      const request = requests.get(this);
      if (request && String(name).toLowerCase() === 'x-redd-cart') request.owned = true;
      return originalHeader.apply(this, arguments);
    };
    prototype.send = function () {
      const request = requests.get(this);
      if (request?.add && !request.owned) this.addEventListener('load', () => {
        if (this.status < 200 || this.status >= 300) return;
        try { addedResponse(this.responseType === 'json' ? this.response : JSON.parse(this.responseText)); } catch (_) { /* The caller owns errors. */ }
      }, { once: true });
      return originalSend.apply(this, arguments);
    };
  }
  if (onCartPage) void open();
})();
